import path from "node:path";
import {
  nodeIsInert,
  nodeIsOptional,
  nodeLabel,
  nodeRunKind,
  nodeSkillAsset,
} from "@skaile/workspaces/factory-assets/connectors/flow/engine";
import { Command } from "commander";
import { discoverFlows } from "../flow-discovery.ts";
import { logErr, logInfo, S } from "../helpers.ts";

/**
 * Create the `skaile flow` command group.
 *
 * Browses flows discovered across every content root via {@link discoverFlows}
 * (the project's `.skaile/` install root, bundled factory-assets when installed /
 * the monorepo tree in dev, user libraries, and the global `~/.skaile/` install
 * root). Subcommands: `list` (print all available flows with their IDs, names,
 * and descriptions) and `show <id>` (print the flow's metadata and its skill
 * nodes in execution order, marking optional nodes).
 *
 * Both take `--project-dir` and forward it to discovery, so they list exactly the
 * flows `skaile run --project-dir <same>` can start.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/flow#make-flow-command
 */
export function makeFlowCommand(): Command {
  const cmd = new Command("flow").description("Manage flows");

  cmd
    .command("list")
    .description("List available flows")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action(async (opts) => {
      const flows = discoverFlows(path.resolve(opts.projectDir));
      if (flows.length === 0) {
        logInfo("No flows found.");
        return;
      }

      console.log();
      console.log(S.heading("  Available Flows"));
      console.log(`  ${S.rule(50)}`);
      for (const f of flows) {
        console.log(`  ${S.cmd(f.id.padEnd(24))} ${f.name}`);
        if (f.description) console.log(`  ${"".padEnd(24)} ${S.dim(f.description)}`);
      }
      console.log();
    });

  cmd
    .command("show <id>")
    .description("Show flow details and node graph")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action(async (id: string, opts) => {
      const flows = discoverFlows(path.resolve(opts.projectDir));
      const flow = flows.find((f) => f.id === id);
      if (!flow) {
        logErr(`Flow not found: ${id}`);
        process.exit(1);
      }

      console.log();
      console.log(`  ${S.heading(flow.name)} ${S.dim(`(${flow.id} v${flow.version})`)}`);
      if (flow.description) console.log(`  ${S.dim(flow.description)}`);
      console.log(`\n  ${S.heading("Nodes")} ${S.dim(`(${flow.nodes.length})`)}`);
      console.log(`  ${S.rule(50)}`);
      // Grow the label column past its 24-char floor to fit the longest label:
      // labels are authored prose and routinely overrun a fixed width, and one
      // long label otherwise knocks that row's remaining columns out of line.
      const labelWidth = Math.max(24, ...flow.nodes.map((n) => nodeLabel(n).length));
      for (const node of flow.nodes) {
        const skill = nodeSkillAsset(node);
        const asset = skill ? ` ${S.label("skill:")} ${S.cmd(skill.slice("skill:".length))}` : "";
        // A normalized legacy placeholder is neither the router nor the optional
        // step those fields now claim, so report what it actually is.
        const inert = nodeIsInert(node);
        const opt = !inert && nodeIsOptional(node) ? S.dim(" [optional]") : "";
        const tail = `${asset}${opt}`;
        // Only pad the kind column when something follows it — the styling wraps
        // the padding in escape codes, so a trailing trim can no longer see it.
        const kind = inert ? "inert" : nodeRunKind(node);
        console.log(
          `  ${nodeLabel(node).padEnd(labelWidth)} ${S.dim(tail ? kind.padEnd(10) : kind)}${tail}`,
        );
      }
      console.log();
    });

  cmd.action(() => cmd.help());

  return cmd;
}
